import { SettingsProps } from 'client/components/core/Sidebar/components/Settings/entities'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import { mimeTypes } from 'client/constants/editorSettings'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { AudioInterface } from 'common/types/entities/AudioInterface'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import {
  OptionSingleValueType,
  SelectNew,
} from 'src/editor/shared/ui-lib/select-new/select-new'
import { FileUpload } from '../components'
import Input from '../components/Input/Input'
import { useUpdateEntity, useUpdateProp } from '../hooks/useUpdateProps'
import DeviceAppearanceNew from '../options/DeviceAppearance/DeviceAppearanceNew'

enum AudioType {
  File = 'file',
  Url = 'url',
}

const audioTypesNames = {
  [AudioType.File]: 'entity_settings.audio.source_type.upload',
  [AudioType.Url]: 'entity_settings.audio.source_type.direct_link',
}

function detectAudioType(entity: AudioInterface) {
  if (entity.fileId) {
    return AudioType.File
  }
  return AudioType.Url
}

function AudioSettingsNew({
  entity,
  defaultEntity,
}: SettingsProps<AudioInterface>) {
  const [audioType, setAudioType] = useState(detectAudioType(entity))
  const updateProp = useUpdateProp(entity)
  const { isMobile } = useDeviceModeContext()
  const updateEntity = useUpdateEntity<AudioInterface>()
  const { t } = useTranslation()

  function getOption(audioType: AudioType) {
    return {
      value: audioType,
      label: t(audioTypesNames[audioType]),
    }
  }

  function updateAudioType(option: OptionSingleValueType) {
    if (!option) {
      return
    }

    const updatedEntity = { ...entity }
    switch (option.value) {
      case AudioType.Url:
        // biome-ignore lint/performance/noDelete: <explanation>
        delete updatedEntity.fileId
        break
      case AudioType.File:
        // biome-ignore lint/performance/noDelete: <explanation>
        delete updatedEntity.url
        break
      default:
    }
    updateEntity(updatedEntity)
    setAudioType(option.value as AudioType)
  }

  return (
    <React.Fragment>
      <SelectNew
        selectedOption={getOption(audioType)}
        onChange={updateAudioType}
        labelText="entity_settings.audio.source_type.choose_source"
        options={[getOption(AudioType.Url), getOption(AudioType.File)]}
        isMulti={false}
      />
      {audioType === AudioType.Url && (
        <Input label="url" value={entity.url} update={updateProp('url')} />
      )}
      {audioType === AudioType.File && (
        <FileUpload
          fileId={entity.fileId}
          mimeTypes={mimeTypes.audio}
          // biome-ignore lint/suspicious/noExplicitAny: <explanation>
          afterUpload={(file: any) => updateProp('fileId')(file.id)}
          label="audio"
          fileType="audio"
        />
      )}
      <SizeAndPositionGroup
        margin={isMobile ? entity.mobileMargin : entity.margin}
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
        defaultMargin={
          isMobile ? defaultEntity?.mobileMargin : defaultEntity?.margin
        }
      />
      <DeviceAppearanceNew entity={entity} />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </React.Fragment>
  )
}

export default AudioSettingsNew
